// src/services/tenantService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

class TenantService {
  constructor() {
    this.collectionName = 'tenants';
  }

  // Create a new tenant
  async createTenant(tenantData) {
    if (!db) throw new Error('Firebase not available');

    try {
      const tenant = {
        ...tenantData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
        settings: {
          // Default clinic settings
          businessHours: {
            monday: { open: '09:00', close: '17:00', closed: false },
            tuesday: { open: '09:00', close: '17:00', closed: false },
            wednesday: { open: '09:00', close: '17:00', closed: false },
            thursday: { open: '09:00', close: '17:00', closed: false },
            friday: { open: '09:00', close: '17:00', closed: false },
            saturday: { open: '09:00', close: '13:00', closed: false },
            sunday: { open: '10:00', close: '16:00', closed: true }
          },
          appointmentDuration: 60,
          currency: 'GBP',
          timezone: 'Europe/London',
          treatmentTypes: [
            { id: 'physiotherapy', name: 'Physiotherapy', duration: 60, price: 150 },
            { id: 'massage', name: 'Massage Therapy', duration: 45, price: 120 },
            { id: 'consultation', name: 'Initial Consultation', duration: 90, price: 200 }
          ],
          rooms: [
            { id: 'room1', name: 'Treatment Room 1', capacity: 1 },
            { id: 'room2', name: 'Treatment Room 2', capacity: 1 }
          ],
          ...tenantData.settings
        }
      };

      const docRef = await addDoc(collection(db, this.collectionName), tenant);
      
      // Initialize tenant subcollections with empty documents to ensure they exist
      await this.initializeTenantCollections(docRef.id);
      
      return { id: docRef.id, ...tenant };
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  }

  // Initialize empty subcollections for a new tenant
  async initializeTenantCollections(tenantId) {
    const collections = ['clients', 'appointments', 'staff', 'invoices', 'soapNotes', 'waitlist', 'rooms'];
    
    for (const collectionName of collections) {
      try {
        // Create a placeholder document that will be deleted later
        await addDoc(collection(db, `tenants/${tenantId}/${collectionName}`), {
          _placeholder: true,
          createdAt: serverTimestamp()
        });
      } catch (error) {
        console.warn(`Warning: Could not initialize ${collectionName} for tenant ${tenantId}:`, error);
      }
    }
  }

  // Get all tenants
  async getAllTenants() {
    if (!db) return [];

    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting tenants:', error);
      return [];
    }
  }

  // Get tenant by ID
  async getTenantById(tenantId) {
    if (!db || !tenantId) return null;

    try {
      const docRef = doc(db, this.collectionName, tenantId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  // Update tenant
  async updateTenant(tenantId, updates) {
    if (!db || !tenantId) throw new Error('Invalid parameters');

    try {
      const docRef = doc(db, this.collectionName, tenantId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return await this.getTenantById(tenantId);
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }

  // Delete tenant (soft delete by setting status to inactive)
  async deleteTenant(tenantId) {
    if (!db || !tenantId) throw new Error('Invalid parameters');

    try {
      const docRef = doc(db, this.collectionName, tenantId);
      await updateDoc(docRef, {
        status: 'inactive',
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  }

  // Get tenants by status
  async getTenantsByStatus(status = 'active') {
    if (!db) return [];

    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', status)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting tenants by status:', error);
      return [];
    }
  }

  // Update tenant settings
  async updateTenantSettings(tenantId, settings) {
    if (!db || !tenantId) throw new Error('Invalid parameters');

    try {
      const docRef = doc(db, this.collectionName, tenantId);
      await updateDoc(docRef, {
        settings: settings,
        updatedAt: serverTimestamp()
      });
      
      return await this.getTenantById(tenantId);
    } catch (error) {
      console.error('Error updating tenant settings:', error);
      throw error;
    }
  }

  // Check if tenant slug is available
  async isSlugAvailable(slug, excludeTenantId = null) {
    if (!db || !slug) return false;

    try {
      let q = query(
        collection(db, this.collectionName),
        where('slug', '==', slug)
      );

      const querySnapshot = await getDocs(q);
      
      // If excluding a tenant (for updates), filter it out
      if (excludeTenantId) {
        return !querySnapshot.docs.some(doc => doc.id !== excludeTenantId);
      }
      
      return querySnapshot.empty;
    } catch (error) {
      console.error('Error checking slug availability:', error);
      
      // For super admin or permission errors, allow slug creation
      // Multi-tenant system should allow creating tenants!
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        console.warn('⚠️ Permission error during slug check - allowing for super admin access');
        return true; // Allow slug to be used
      }
      
      return false; // Only fail for actual technical errors
    }
  }

  // Generate a unique slug from tenant name
  generateSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim('-'); // Remove leading/trailing hyphens
  }
}

// Export singleton instance
const tenantService = new TenantService();
export { tenantService };
export default tenantService;
